import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE } from '../App';

// ── Password strength ────────────────────────────────────────────────────────

const SPECIAL_CHAR_RE = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

function getPasswordStrength(password) {
  if (!password) return null;
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (SPECIAL_CHAR_RE.test(password)) score++;
  if (password.length >= 12) score++;
  if (score <= 2) return { label: 'Weak',   color: '#FF766B', width: '33%' };
  if (score === 3) return { label: 'Fair',   color: '#F1FF5C', width: '66%' };
  return            { label: 'Strong', color: '#BDFFC0', width: '100%' };
}

// ── Static styles (defined outside component — no re-creation on each render) ─

const labelStyle = { fontSize: '13px', color: '#AAB0B8', display: 'block' };
const errorStyle  = { fontSize: '11px', color: '#FF766B', marginTop: '4px' };
const hintStyle   = { fontSize: '11px', color: '#AAB0B8', marginTop: '4px' };

function inputStyle(hasError) {
  return {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: `0.5px solid ${hasError ? '#FF766B' : '#3D4349'}`,
    background: '#22272D', color: '#F4FAFF', fontSize: '13px',
    fontFamily: 'Manrope, sans-serif', outline: 'none',
    marginTop: '6px', boxSizing: 'border-box',
  };
}

function ShowHideBtn({ onClick, visible }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        position: 'absolute', right: '10px', top: '50%',
        transform: 'translateY(-50%)', background: 'none',
        border: 'none', cursor: 'pointer',
        color: '#AAB0B8', fontSize: '12px', fontFamily: 'Manrope, sans-serif',
      }}
    >
      {visible ? 'Hide' : 'Show'}
    </button>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

function Setup({ onComplete }) {
  const [firstName,    setFirstName]    = useState('');
  const [lastName,     setLastName]     = useState('');
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirm,      setConfirm]      = useState('');
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [errors,       setErrors]       = useState({});
  const [loading,      setLoading]      = useState(false);

  const strength      = getPasswordStrength(password);
  const confirmsMatch = confirm.length > 0 && confirm === password;
  const confirmsDiff  = confirm.length > 0 && confirm !== password;

  // Clear a single field error when the user starts typing
  const clearErr = useCallback((field) => {
    setErrors(prev => {
      if (!prev[field]) return prev;   // avoid unnecessary re-render
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // ── Validation ─────────────────────────────────────────────────────────────

  const validate = useCallback(() => {
    const e      = {};
    const nameRe = /^[a-zA-Z\s]+$/;

    if (!firstName.trim())            e.firstName = 'First name is required';
    else if (!nameRe.test(firstName)) e.firstName = 'No numbers or special characters allowed';

    if (!lastName.trim())             e.lastName  = 'Last name is required';
    else if (!nameRe.test(lastName))  e.lastName  = 'No numbers or special characters allowed';

    if (!email.trim())                e.email     = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
                                      e.email     = 'Invalid email address';

    if (!password)                    e.password  = 'Password is required';
    else if (password.length < 8)     e.password  = 'Must be at least 8 characters';
    else if (!/[A-Z]/.test(password)) e.password  = 'Must include at least one uppercase letter';
    else if (!/[0-9]/.test(password)) e.password  = 'Must include at least one number';
    else if (!SPECIAL_CHAR_RE.test(password))
                                      e.password  = 'Must include at least one special character';

    if (!confirm)                     e.confirm   = 'Please confirm your password';
    else if (confirm !== password)    e.confirm   = 'Passwords do not match';

    return e;
  }, [firstName, lastName, email, password, confirm]);

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSetup = useCallback(async () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setErrors({});
    setLoading(true);
    try {
      const setupRes = await axios.post(`${API_BASE}/api/setup`, {
        name:     `${firstName.trim()} ${lastName.trim()}`,
        email:    email.trim().toLowerCase(),
        password,
      });
      if (setupRes.data.success) {
        const loginRes = await axios.post(
          `${API_BASE}/api/login`,
          { email: email.trim().toLowerCase(), password },
          { withCredentials: true }
        );
        onComplete(loginRes.data.success ? loginRes.data.user : undefined);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Setup failed. Please try again.';
      if (msg.toLowerCase().includes('email')) {
        setErrors({ email: 'This email is already registered.' });
      } else {
        setErrors({ general: msg });
      }
    } finally {
      // Always runs — prevents button being stuck in loading state
      setLoading(false);
    }
  }, [validate, firstName, lastName, email, password, onComplete]);

  // ── Derived state ──────────────────────────────────────────────────────────

  const isFormFilled =
    firstName.trim() && lastName.trim() &&
    email.trim() && password && confirm;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{
      minHeight: '100vh', background: '#22272D',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: '40px 20px',
    }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '28px' }}>
        <div style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: '12px', marginBottom: '8px',
        }}>
          <img src="/Xicon.png" alt="X Meeting" style={{ width: '40px' }} />
          <div style={{
            fontFamily: 'Bebas Neue, sans-serif', fontSize: '40px',
            fontWeight: '400', color: '#F4FAFF', letterSpacing: '2.0px',
          }}>
            MEETING
          </div>
        </div>
        <div style={{ fontSize: '13px', color: '#AAB0B8', marginTop: '4px' }}>
          First time setup — create your super admin account
        </div>
      </div>

      {/* Card */}
      <div style={{
        background: '#2D3339', border: '0.5px solid #3D4349',
        borderRadius: '16px', padding: '36px', width: '100%', maxWidth: '440px',
      }}>

        {/* Name row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={labelStyle}>First name</label>
            <input
              autoComplete="given-name"
              value={firstName}
              onChange={e => { setFirstName(e.target.value); clearErr('firstName'); }}
              style={inputStyle(!!errors.firstName)}
            />
            {errors.firstName && <div style={errorStyle}>{errors.firstName}</div>}
          </div>
          <div>
            <label style={labelStyle}>Last name</label>
            <input
              autoComplete="family-name"
              value={lastName}
              onChange={e => { setLastName(e.target.value); clearErr('lastName'); }}
              style={inputStyle(!!errors.lastName)}
            />
            {errors.lastName && <div style={errorStyle}>{errors.lastName}</div>}
          </div>
        </div>

        {/* Email */}
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Email</label>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={e => { setEmail(e.target.value); clearErr('email'); }}
            style={inputStyle(!!errors.email)}
          />
          {errors.email && <div style={errorStyle}>{errors.email}</div>}
        </div>

        {/* Password */}
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              onChange={e => { setPassword(e.target.value); clearErr('password'); }}
              style={{ ...inputStyle(!!errors.password), paddingRight: '48px' }}
            />
            <ShowHideBtn onClick={() => setShowPassword(v => !v)} visible={showPassword} />
          </div>

          {/* Strength bar — reserved space so layout never jumps */}
          <div style={{ marginTop: '8px', minHeight: '28px' }}>
            {password && strength ? (
              <>
                <div style={{
                  height: '4px', borderRadius: '4px',
                  background: '#3D4349', overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%', borderRadius: '4px',
                    width: strength.width, background: strength.color,
                    transition: 'width 0.3s ease, background 0.3s ease',
                  }} />
                </div>
                <div style={{ fontSize: '11px', color: strength.color, marginTop: '4px' }}>
                  {strength.label}
                </div>
              </>
            ) : (
              !password && <div style={hintStyle}>Min. 8 chars · 1 uppercase · 1 number · 1 special character</div>
            )}
          </div>

          {errors.password && <div style={errorStyle}>{errors.password}</div>}
        </div>

        {/* Confirm password */}
        <div style={{ marginBottom: '28px' }}>
          <label style={labelStyle}>Confirm password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirm}
              onChange={e => { setConfirm(e.target.value); clearErr('confirm'); }}
              onKeyDown={e => e.key === 'Enter' && !loading && isFormFilled && handleSetup()}
              style={{ ...inputStyle(!!errors.confirm), paddingRight: '48px' }}
            />
            <ShowHideBtn onClick={() => setShowConfirm(v => !v)} visible={showConfirm} />
          </div>

          {/* Live match feedback */}
          {confirmsMatch && (
            <div style={{ fontSize: '11px', color: '#BDFFC0', marginTop: '4px' }}>
              ✓ Passwords match
            </div>
          )}
          {confirmsDiff && !errors.confirm && (
            <div style={{ fontSize: '11px', color: '#FF766B', marginTop: '4px' }}>
              Passwords do not match
            </div>
          )}
          {errors.confirm && <div style={errorStyle}>{errors.confirm}</div>}
        </div>

        {/* General error */}
        {errors.general && (
          <div style={{
            padding: '10px 14px', borderRadius: '8px', marginBottom: '16px',
            background: 'rgba(255,118,107,0.15)', color: '#FF766B', fontSize: '13px',
          }}>
            {errors.general}
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSetup}
          disabled={loading || !isFormFilled}
          style={{
            width: '100%', padding: '11px', borderRadius: '8px',
            background: '#F1FF5C', color: '#22272D', border: 'none',
            fontSize: '14px', fontWeight: '600', fontFamily: 'Manrope, sans-serif',
            cursor: loading || !isFormFilled ? 'not-allowed' : 'pointer',
            opacity: loading || !isFormFilled ? 0.7 : 1,
            transition: 'opacity 0.2s ease',
          }}
        >
          {loading ? 'Creating account...' : 'Create Super Admin Account'}
        </button>
      </div>

      {/* Footer */}
      <div style={{
        marginTop: '32px', display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: '8px',
      }}>
        <img src="/WEBXPAY_Master_logo-03.png" alt="WEBXPAY" style={{ height: '15px' }} />
        <div style={{ fontSize: '12px', color: '#AAB0B8' }}>Powered by WEBXPAY (PVT) LTD</div>
      </div>
    </div>
  );
}

export default Setup;